<template>
  <click-outside @click-outside="onClickOutside">
    <div class="searchable-datatable">
      <div class="full-width relative">
        <i class="fa-solid fa-magnifying-glass search-icon"></i>
        <text-field
          v-model="searchVal"
          class="mb-0"
          label-class="full-width"
          input-class="pl-6 pr-4"
          placeholder="Start Typing..."
          @focus="open = true"
        ></text-field>
        <Transition name="fade">
          <i
            v-if="searchVal"
            class="fa-solid fa-xmark clear-icon"
            @click="clearSearch"
          ></i>
        </Transition>
      </div>
      <Transition name="slide-down-fade">
        <div v-if="open" class="search-results p-2">
          <DatatableV3
            :id="id"
            ref="datatable"
            :source="source"
            :filters="filters"
            :config="config"
            :map-records="applyFilter"
            no-download
            hide-filters
            hide-page-length-select
            reset-paging
          ></DatatableV3>
        </div>
      </Transition>
    </div>
  </click-outside>
</template>

<script>
import debounce from "lodash/debounce";
import { getQueryVariables } from "@/lib/url.js";

import ClickOutside from "@/components/ClickOutside.vue";
import DatatableV3 from "@/components/DatatableV3.vue";
import TextField from "@/components/TextField.vue";
import Transition from "@/components/Transition.vue";

export default {
  components: {
    TextField,
    DatatableV3,
    Transition,
    ClickOutside
  },
  props: {
    modelValue: {
      type: String,
      default: ""
    },
    source: {
      type: String,
      default: ""
    },
    config: {
      type: Object,
      default: null
    },
    mapRecords: {
      type: Function,
      default: () => {}
    },
    filters: {
      type: Array,
      default: () => []
    },
    id: {
      type: String,
      default: ""
    }
  },
  emits: ["update:modelValue"],
  data() {
    return {
      open: false
    };
  },
  computed: {
    searchVal: {
      get() {
        return this.modelValue;
      },
      set(val) {
        return this.$emit("update:modelValue", val);
      }
    }
  },
  watch: {
    modelValue(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.handleSearch();
      }
    }
  },
  methods: {
    clearSearch() {
      this.searchVal = "";
    },
    handleSearch: debounce(function () {
      const datatable = this.$refs.datatable;
      if (datatable?.search) datatable.search(this.searchVal);
    }, 500),
    applyFilter(config) {
      // Auto apply search on initial load
      const _config = this.mapRecords ? this.mapRecords(config) : config;
      const params = getQueryVariables();
      if (this.searchVal) {
        _config["search"]["value"] = this.searchVal;
      }
      // Forward the generic "term" search param to the backend
      if (params.term) _config.term = params.term;

      if (this.filters) {
        // Apply datatable filters based on url params
        this.filters.forEach((filter) => {
          const filterValue = params[this.getFilterParamName(filter)];
          if (filterValue) {
            _config[filter.name] = filterValue;
          }
        });
      }

      return _config;
    },
    getFilterParamName(filter) {
      return `${this.id}_${filter.name}`;
    },
    onClickOutside(event) {
      if (this.shouldIgnoreClick(event)) return;
      this.open = false;
    },
    shouldIgnoreClick(event) {
      const elementsToPersist = [
        ".mx-datepicker",
        ".mx-datepicker-popup",
        ".mx-calendar",
        ".select-box",
        ".option",
        ".select-options",
        ".option-row",
        ".bg-blue-100"
      ].join(", ");

      return !!event.target.closest(elementsToPersist);
    }
  }
};
</script>

<style lang="scss" scoped>
.searchable-datatable {
  position: relative;
}
.search-icon {
  position: absolute;
  top: calc(50% + 1px);
  left: 8px;
  transform: translateY(-50%);
  color: var(--secondary-color);
}
.clear-icon {
  position: absolute;
  top: calc(50% + 1px);
  right: 8px;
  transform: translateY(-50%);
  cursor: pointer;
  font-size: 1.1em;
}
.search-results {
  position: absolute;
  width: 100%;
  top: 36px;
  left: 0;
  z-index: 2;
  background: #fff;
  box-shadow: var(--box-shadow);
}
:deep(.dt-info),
:deep(.dt-paging) {
  padding-bottom: 1rem;
  display: inline-flex !important;
}
:deep(.dt-info) {
  width: calc(100% - 330px) !important;
}
</style>
